import { useEffect, useMemo, useRef, useState } from 'react';

import { AxiosError } from 'axios';

import { getBackendErrorMessage } from '@/shared/lib/errors';

import { SEARCH_CATEGORY_ORDER } from '../model/categories';
import { LaneStatus, SearchCategory, SearchLaneState } from '../model/types';
import useSearchService from './search.service';

export const SEARCH_MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 180;

interface SearchStreamState {
  lanes: Partial<Record<SearchCategory, SearchLaneState>>;
  failedLanes: Partial<Record<SearchCategory, LaneStatus>>;
  isStreaming: boolean;
  /** A stream that ends without a `done` frame is incomplete, not empty — the
   *  distinction is what stops the UI claiming "nothing matched" after a cut stream. */
  isComplete: boolean;
  error: string | null;
}

const EMPTY_STATE: SearchStreamState = {
  lanes: {},
  failedLanes: {},
  isStreaming: false,
  isComplete: false,
  error: null,
};

/**
 * Debounced live search over the NDJSON stream.
 *
 * Every keystroke aborts the in-flight stream. The server cannot itself be cancelled
 * (its statement timeout is the only real cancel), but dropping the reader is what
 * keeps the rendered lanes honest about the query currently in the box.
 */
export function useSearchStream(query: string) {
  const { streamSearch } = useSearchService();
  const [state, setState] = useState<SearchStreamState>(EMPTY_STATE);
  const abortRef = useRef<AbortController | null>(null);

  const trimmedQuery = query.trim();
  const isQueryLongEnough = trimmedQuery.length >= SEARCH_MIN_QUERY_LENGTH;

  useEffect(() => {
    abortRef.current?.abort();

    if (!isQueryLongEnough) {
      setState(EMPTY_STATE);
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;

    const timer = window.setTimeout(async () => {
      setState({ ...EMPTY_STATE, isStreaming: true });

      try {
        await streamSearch(
          { query: trimmedQuery },
          (frame) => {
            switch (frame.type) {
              case 'category':
                setState((previous) => ({
                  ...previous,
                  lanes: {
                    ...previous.lanes,
                    [frame.category]: {
                      hits: frame.hits,
                      totalCount: frame.totalCount,
                      tookMs: frame.tookMs,
                    },
                  },
                }));
                break;

              case 'laneFailed':
                setState((previous) => ({
                  ...previous,
                  failedLanes: { ...previous.failedLanes, [frame.category]: frame.status },
                }));
                break;

              case 'done':
                setState((previous) => ({ ...previous, isComplete: true }));
                break;

              default:
                break;
            }
          },
          controller.signal,
        );
      } catch (error) {
        if (!controller.signal.aborted) {
          setState((previous) => ({
            ...previous,
            error: getBackendErrorMessage(error as AxiosError),
          }));
        }
      } finally {
        if (!controller.signal.aborted) {
          setState((previous) => ({ ...previous, isStreaming: false }));
        }
      }
    }, DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [trimmedQuery, isQueryLongEnough, streamSearch]);

  /** Enum order, not arrival order, so rows do not jump while frames land. */
  const populatedLanes = useMemo(
    () =>
      SEARCH_CATEGORY_ORDER.map((category) => ({ category, lane: state.lanes[category] })).filter(
        (entry): entry is { category: SearchCategory; lane: SearchLaneState } =>
          Boolean(entry.lane && entry.lane.hits.length > 0),
      ),
    [state.lanes],
  );

  return {
    ...state,
    populatedLanes,
    isQueryLongEnough,
    trimmedQuery,
  };
}
